import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { PermissionGate } from '../../components/PermissionGate'
import { roadmapApi } from '../../api/roadmap.api'
import { RoadmapStatusBadge } from './RoadmapStatusBadge'
import { RoadmapEntryFormModal } from './RoadmapEntryFormModal'
import type { RoadmapEntry } from '../../api/types'
import styles from './Roadmap.module.css'
import projectStyles from '../projects/ProjectDetail.module.css'

type ViewMode = 'timeline' | 'list'

export function RoadmapTab({ projectId }: { projectId: string }) {
  const [view, setView] = useState<ViewMode>('timeline')
  const [formEntry, setFormEntry] = useState<RoadmapEntry | null | undefined>(undefined)

  const entriesQuery = useQuery({ queryKey: ['roadmap', projectId], queryFn: async () => (await roadmapApi.list(projectId)).data })
  const entries = entriesQuery.data ?? []

  return (
    <div>
      <div className={projectStyles.filterBar}>
        <button className={[projectStyles.tab, view === 'timeline' ? projectStyles.tabActive : ''].join(' ')} onClick={() => setView('timeline')}>
          Zeitachse
        </button>
        <button className={[projectStyles.tab, view === 'list' ? projectStyles.tabActive : ''].join(' ')} onClick={() => setView('list')}>
          Liste
        </button>
        <PermissionGate anyOf={['roadmap.manage']}>
          <Button size="sm" variant="primary" onClick={() => setFormEntry(null)} style={{ marginLeft: 'auto' }}>
            + Roadmap-Eintrag
          </Button>
        </PermissionGate>
      </div>

      {entries.length === 0 ? (
        <EmptyState mascot title="Noch keine Roadmap" description="Lege Phasen und Meilensteine für dieses Projekt an." />
      ) : view === 'timeline' ? (
        <div className={styles.timeline}>
          {entries.map((entry, index) => (
            <div key={entry.id} className={styles.timelineItem} onClick={() => setFormEntry(entry)}>
              <div className={styles.timelineRail}>
                <div className={styles.timelineDot} style={{ background: entry.status === 'COMPLETED' ? 'var(--success)' : 'var(--accent-9)' }} />
                {index < entries.length - 1 ? <div className={styles.timelineLine} /> : null}
              </div>
              <div className={styles.timelineCard}>
                <div className={styles.phaseLabel}>{entry.phaseLabel}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 4 }}>
                  <h3 style={{ margin: 0 }}>{entry.title}</h3>
                  <RoadmapStatusBadge status={entry.status} />
                </div>
                {entry.goal ? <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', margin: '4px 0' }}>{entry.goal}</p> : null}
                <div style={{ display: 'flex', gap: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {entry.startDate ? <span>Start: {new Date(entry.startDate).toLocaleDateString('de-DE')}</span> : null}
                  {entry.targetDate ? <span>Ziel: {new Date(entry.targetDate).toLocaleDateString('de-DE')}</span> : null}
                  <span>{entry.tasks.length} Aufgaben</span>
                </div>
                <div className={styles.progressBar}>
                  <div className={styles.progressFill} style={{ width: `${entry.progress}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div>
          {entries.map((entry) => (
            <div key={entry.id} className={styles.listRow} onClick={() => setFormEntry(entry)}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', minWidth: 70 }}>{entry.phaseLabel}</span>
              <span style={{ flex: 1, fontWeight: 600 }}>{entry.title}</span>
              <RoadmapStatusBadge status={entry.status} />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{entry.progress}%</span>
            </div>
          ))}
        </div>
      )}

      {formEntry !== undefined ? <RoadmapEntryFormModal projectId={projectId} entry={formEntry} onClose={() => setFormEntry(undefined)} /> : null}
    </div>
  )
}
